import { useId, useState } from 'react';
import { Input } from './Input';
import { SearchableSelect } from './SearchableSelect';
import {
  composePhoneNumber,
  digitsOf,
  findPhoneCountry,
  MAX_PHONE_EXTENSION_LENGTH,
  parsePhoneNumber,
  PHONE_COUNTRIES,
  type PhoneNumberParts,
} from './phoneNumber';

const COUNTRY_OPTIONS = PHONE_COUNTRIES.map((phoneCountry) => ({
  value: phoneCountry.key,
  label: `${phoneCountry.flag} ${phoneCountry.name} +${phoneCountry.dialCode}`,
}));

type PhoneNumberInputProps = {
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  extensionLabel: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  dataCy?: string;
};

export function PhoneNumberInput({
  name,
  value,
  onChange,
  extensionLabel,
  label,
  error,
  disabled,
  dataCy,
}: Readonly<PhoneNumberInputProps>) {
  const labelId = useId();
  const [parts, setParts] = useState<PhoneNumberParts>(() =>
    parsePhoneNumber(value)
  );
  const [lastSeenValue, setLastSeenValue] = useState(value);

  if (value !== lastSeenValue) {
    setLastSeenValue(value);
    if (value !== composePhoneNumber(parts)) {
      setParts(parsePhoneNumber(value, parts.countryKey));
    }
  }

  const selectedCountry = findPhoneCountry(parts.countryKey);

  const applyParts = (nextParts: PhoneNumberParts) => {
    const nextValue = composePhoneNumber(nextParts);
    setParts(nextParts);
    setLastSeenValue(nextValue);
    onChange(name, nextValue);
  };

  const handleCountryChange = (_countryFieldName: string, countryKey: string) =>
    applyParts({ ...parts, countryKey });

  const handleNumberChange = (
    _numberFieldName: string,
    typedNumber: string
  ) => {
    const typedParts = parsePhoneNumber(typedNumber, parts.countryKey);
    applyParts({
      ...typedParts,
      extension: typedParts.extension || parts.extension,
    });
  };

  const handleExtensionChange = (
    _extensionFieldName: string,
    typedExtension: string
  ) =>
    applyParts({
      ...parts,
      extension: digitsOf(typedExtension).slice(0, MAX_PHONE_EXTENSION_LENGTH),
    });

  return (
    <div className="flex flex-col w-full gap-2">
      {label !== undefined && (
        <span
          id={labelId}
          className="ml-md mb-xxs text-sm font-medium text-neut-prim"
        >
          {label}
        </span>
      )}
      <div className="flex items-start gap-2">
        <div className="w-28 shrink-0">
          <SearchableSelect
            name={`${name}Country`}
            value={selectedCountry.key}
            selectedDisplay={`${selectedCountry.flag} +${selectedCountry.dialCode}`}
            options={COUNTRY_OPTIONS}
            onChange={handleCountryChange}
            disabled={disabled}
            className="px-3"
            panelClassName="min-w-64"
            dataCy={dataCy === undefined ? undefined : `${dataCy}-country`}
          />
        </div>
        <Input
          type="tel"
          autoComplete="tel-national"
          name={name}
          value={parts.nationalNumber}
          onChange={handleNumberChange}
          error={error}
          disabled={disabled}
          dataCy={dataCy}
          aria-labelledby={label === undefined ? undefined : labelId}
        />
        <div className="w-24 shrink-0">
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            name={`${name}Extension`}
            value={parts.extension}
            onChange={handleExtensionChange}
            placeholder={extensionLabel}
            maxLength={MAX_PHONE_EXTENSION_LENGTH}
            disabled={disabled}
            dataCy={dataCy === undefined ? undefined : `${dataCy}-extension`}
            aria-label={extensionLabel}
          />
        </div>
      </div>
    </div>
  );
}
